import { buildProps, definePropType } from '@sgui-plus/utils/props'
import { isBool } from '@sgui-plus/utils/util'
import {
  TooltipTriggerType,
  useTooltipContentProps
} from '@sgui-plus/components/tooltip'
import type { Placement } from '@sgui-plus/components/popper'
import type { Options } from '@popperjs/core'
import type { ExtractPropTypes, PropType, StyleValue } from 'vue'
import type Popover from './popover.vue'

type ClassObjectType = Record<string, any>
type ClassType = string | ClassObjectType | ClassType[]

export const popoverProps = buildProps({
  trigger: {
    type: [String, Array] as PropType<TooltipTriggerType | TooltipTriggerType[]>,
    default: 'hover',
  },
  placement: {
    type: definePropType<Placement>(String),
    default: 'bottom',
  },
  disabled: {
    type: Boolean
  },
  visible: useTooltipContentProps.visible,
  transition: useTooltipContentProps.transition,
  popperOptions: {
    type: definePropType<Partial<Options>>(Object),
    default: () => ({}),
  },
  tabindex: {
    type: definePropType<number | string>([Number, String]),
    default: 0,
  },
  content: useTooltipContentProps.content,
  popperStyle: {
    type: definePropType<StyleValue>([String, Array, Object]),
  },
  popperClass: {
    type: definePropType<ClassType>([String, Array, Object]),
  },
  enterable: {
    type: Boolean,
    default: true,
  },
  theme: {
    type: String,
    default: 'light',
  },
  transfer: {
    type: Boolean,
    default: true,
  },
  title: String,

  width: {
    type: [String, Number],
    default: 150,
  },
  offset: {
    type: Number,
    default: undefined,
  },
  showAfter: {
    type: Number,
    default: 0,
  },
  hideAfter: {
    type: Number,
    default: 200,
  },
  autoClose: {
    type: Number,
    default: 0,
  },
  showArrow: {
    type: Boolean,
    default: true,
  },
  persistent: {
    type: Boolean,
    default: true,
  },
  'onUpdate:visible': {
    type: Function as PropType<(visible: boolean) => void>,
  },
} as const)
export type PopoverProps = ExtractPropTypes<typeof popoverProps>

export const popoverEmits = {
  'update:visible': (value: boolean) => isBool(value),
  'before-enter': () => true,
  'before-leave': () => true,
  'after-enter': () => true,
  'after-leave': () => true,
}
export type PopoverEmits = typeof popoverEmits

export type PopoverInstance = InstanceType<typeof Popover>
